import { useState } from 'react'
import { Link } from 'react-router-dom'
import { localDayKey, sollSecondsForDay, type PublicUserDTO, type WorkPlanDay } from '@wf/shared'
import { useAttendanceMe } from '../attendance/useAttendance'
import { formatDateDE } from '../time/format'
import { CorrectionRequestForm } from '../corrections/CorrectionRequestForm'
import { AbsenceRequestCard } from './AbsenceRequestCard'
import { VacationCard } from './VacationCard'
import { BigStampPad } from './BigStampPad'
import { ReportTable } from './ReportTable'
import { TodayStampsCard } from './TodayStampsCard'
import { WeekChart } from './WeekChart'
import { WorkedTodayCard } from './WorkedTodayCard'
import { liveWorkedSeconds, useDayRolloverInvalidation, useNow } from './live'
import { monthLabel, monthRange, shiftMonth, weekRange } from './timeMath'
import { useAttendanceReport, useTimesheet, useWorkPlan } from './useZeiten'

/** Tagessoll (Sekunden) aus der HEUTE wirksamen Dienstplan-Version (@wf/shared, 1b); kein Solltag → null.
 *  (Vorher wochentags-naiv UND sonntags-blind — mit planbarem Sonntag + Versionen wäre beides falsch.) */
function sollTodaySeconds(plan: WorkPlanDay[] | undefined, today: string): number | null {
  if (!plan) return null
  const sec = sollSecondsForDay(plan, today)
  return sec > 0 ? sec : null
}

/**
 * „Meine Zeit": „Gearbeitet heute" mit Fortschritt zum Tagessoll, großes Stempel-Pad, „Heute
 * gestempelt" + Wochen-Balken nebeneinander — plus (rechtliche Substanz, Art. 15) der eigene
 * Monatsnachweis und der Korrektur-Antrag. Bewusst OHNE Begrüßung/Live-Uhr (Doppelung zur Übersicht)
 * und OHNE Status-Pille (der Stempel-Stand steht in der Header-Uhr, die Verbindung im Kopf-Indikator)
 * — Simon, 08.07.2026: Zeiten soll so clean sein wie Meine Liste. Die schmale, zentrierte Spalte
 * (.wf-zsmine) hält die Karten auch am PC beieinander statt über 1400px zu zerlaufen.
 */
export function MyTimePanel({ user }: { user: PublicUserDTO }) {
  const attendance = useAttendanceMe()
  const now = useNow()
  const today = useDayRolloverInvalidation() // klemmt Snapshot/Queries beim Tageswechsel (Dauer-Tablet)
  const week = weekRange(today)
  const weekReport = useAttendanceReport(week.from, week.to, undefined)
  // Saldo nur bis HEUTE: die volle Wochen-Range würde das Soll künftiger Tage mitzählen — jeder würde
  // die Woche mit dickem Minus beginnen (Review-Fund). Ist künftiger Tage ist ohnehin 0.
  const weekTimesheet = useTimesheet(week.from, today, undefined)
  const plan = useWorkPlan(user.id)

  const [month, setMonth] = useState(() => localDayKey(Date.now()).slice(0, 7))
  const monthR = monthRange(month)
  const monthReport = useAttendanceReport(monthR.from, monthR.to, undefined)
  const [showCorrection, setShowCorrection] = useState(false)
  const [sent, setSent] = useState(false)

  const state = attendance.data
  const todayStamps = (weekReport.data?.stamps ?? []).filter((s) => localDayKey(s.tsMs) === today)
  const liveExtra = state ? liveWorkedSeconds(state, now) - state.todaySeconds : 0

  return (
    <div className="wf-view wf-zsmine">
      {attendance.isError ? (
        <div className="error" role="alert">
          Anwesenheit konnte nicht geladen werden — Stempeln ist gerade nicht möglich.
        </div>
      ) : null}
      {state ? (
        <WorkedTodayCard state={state} sollTodaySeconds={sollTodaySeconds(plan.data?.plan, today)} weekTimesheet={weekTimesheet.data} />
      ) : attendance.isLoading ? (
        <div className="muted">Anwesenheit wird geladen…</div>
      ) : null}
      {state ? <BigStampPad state={state} /> : null}

      {weekReport.isError ? (
        <div className="error" role="alert">
          Stempelungen konnten nicht geladen werden.
        </div>
      ) : weekReport.isLoading ? (
        <div className="muted">Stempelungen werden geladen…</div>
      ) : (
        <div className="wf-zsgrid">
          <TodayStampsCard stamps={todayStamps} />
          {weekReport.data ? (
            <WeekChart weekStart={week.from} days={weekReport.data.days} today={today} liveExtraSeconds={liveExtra} />
          ) : null}
        </div>
      )}
      {weekTimesheet.isError ? (
        <div className="error" role="alert">
          Soll/Ist konnte nicht geladen werden.
        </div>
      ) : null}
      {/* Ganztägig abwesend eingetragen und trotzdem gestempelt: ein Erfassungs-Widerspruch. Die Zeiten
          bleiben unangetastet — aber der Betroffene soll es sehen, denn meist hätte es ein halber Tag
          sein müssen. Bewusst hier statt in TimesheetView: die Komponente wird nirgends gerendert. */}
      {(weekTimesheet.data?.absenceStampConflicts ?? []).length > 0 ? (
        <div className="wf-ztwarn" role="status">
          <span aria-hidden="true">⚠</span>
          <span>
            Du bist am {(weekTimesheet.data?.absenceStampConflicts ?? []).map(formatDateDE).join(', ')} als
            ganztägig abwesend eingetragen und hast trotzdem gestempelt. Bitte sag deinem Chef Bescheid — war
            es ein halber Tag?
          </span>
        </div>
      ) : null}

      <div className="wf-sectionhead">
        <h3>Mein Monatsnachweis</h3>
        <div className="wf-ztnav">
          <button
            type="button"
            className="wf-btn wf-btn--ghost wf-btn--sm"
            aria-label="Voriger Monat"
            onClick={() => setMonth((m) => shiftMonth(m, -1))}
          >
            ◀
          </button>
          <span className="wf-ztnav__month">{monthLabel(month)}</span>
          <button
            type="button"
            className="wf-btn wf-btn--ghost wf-btn--sm"
            aria-label="Nächster Monat"
            onClick={() => setMonth((m) => shiftMonth(m, 1))}
          >
            ▶
          </button>
        </div>
      </div>
      {monthReport.isLoading ? <div className="muted">Nachweis wird geladen…</div> : null}
      {monthReport.isError ? (
        <div className="error" role="alert">
          Nachweis konnte nicht geladen werden.
        </div>
      ) : null}
      {monthReport.data ? <ReportTable report={monthReport.data} /> : null}

      {/* Hinweis + Aktion als EINE Karte: am PC lagen Frage (links), Knopf (rechts unten) und
          Unveränderbarkeits-Hinweis (links unten) in drei Ecken — das gehört zusammen (Simon, 08.07.2026). */}
      <div className="wf-card wf-zscard wf-zsfix">
        <div className="wf-zsfix__text">
          <div className="wf-zscard__label">Stimmt etwas nicht?</div>
          <p className="hint">Original-Stempel sind unveränderbar — Korrekturen bleiben mit Grund und Urheber nachvollziehbar.</p>
        </div>
        {sent ? (
          <span className="wf-corrbar__sent" role="status">
            Anfrage gesendet ✓
          </span>
        ) : null}
        <button
          type="button"
          className="wf-btn wf-btn--ghost"
          aria-expanded={showCorrection}
          onClick={() => {
            setSent(false)
            setShowCorrection((open) => !open)
          }}
        >
          Zeit-Korrektur anfragen
        </button>
      </div>
      {showCorrection ? (
        <CorrectionRequestForm
          onClose={() => setShowCorrection(false)}
          onSent={() => {
            setShowCorrection(false)
            setSent(true)
          }}
        />
      ) : null}
      <VacationCard />
      <AbsenceRequestCard />
      <p className="wf-a13link">
        <Link to="/zeiten/datenschutz">Datenschutz-Info zur Zeiterfassung (Art. 13 DSGVO)</Link>
      </p>
    </div>
  )
}
